import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import FloatingButtons from '../../../components/feature/FloatingButtons';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import InternalLinks from '../../../components/feature/InternalLinks';

export default function SanayiBolgesiKameraSistemi() {
  useEffect(() => {
    document.title = 'Sanayi Bölgesi Kamera Sistemi | Fiber Ek';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'OSB ve sanayi bölgeleri için profesyonel IP kamera sistemleri. 7/24 güvenlik izleme, yüksek çözünürlük ve uzaktan erişim imkanı.');
    }

    // Canonical link
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/sanayi-bolgesi-kamera-sistemi`;
    document.head.appendChild(canonical);

    // Open Graph tags
    const ogTitle = document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    ogTitle.content = 'Sanayi Bölgesi Kamera Sistemi | Fiber Ek';
    document.head.appendChild(ogTitle);

    const ogDescription = document.createElement('meta');
    ogDescription.setAttribute('property', 'og:description');
    ogDescription.content = 'OSB ve sanayi bölgeleri için profesyonel IP kamera sistemleri. 7/24 güvenlik izleme, yüksek çözünürlük ve uzaktan erişim imkanı.';
    document.head.appendChild(ogDescription);

    const ogUrl = document.createElement('meta');
    ogUrl.setAttribute('property', 'og:url');
    ogUrl.content = `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/sanayi-bolgesi-kamera-sistemi`;
    document.head.appendChild(ogUrl);

    const ogType = document.createElement('meta');
    ogType.setAttribute('property', 'og:type');
    ogType.content = 'website';
    document.head.appendChild(ogType);

    const ogLocale = document.createElement('meta');
    ogLocale.setAttribute('property', 'og:locale');
    ogLocale.content = 'tr_TR';
    document.head.appendChild(ogLocale);

    // Service JSON-LD
    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Sanayi Bölgesi Kamera Sistemi",
      "description": "OSB ve sanayi bölgeleri için profesyonel IP kamera sistemleri. 7/24 güvenlik izleme, yüksek çözünürlük ve uzaktan erişim imkanı.",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Fiber Ek",
        "telephone": "+905537165561",
        "url": import.meta.env.VITE_SITE_URL
      },
      "areaServed": {
        "@type": "Place",
        "name": "İstanbul, Kocaeli, Sakarya, Bursa, Tekirdağ, Yalova"
      },
      "url": `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/sanayi-bolgesi-kamera-sistemi`
    });
    document.head.appendChild(schema);

    return () => {
      document.head.removeChild(canonical);
      document.head.removeChild(ogTitle);
      document.head.removeChild(ogDescription);
      document.head.removeChild(ogUrl);
      document.head.removeChild(ogType);
      document.head.removeChild(ogLocale);
      document.head.removeChild(schema);
    };
  }, []);

  const breadcrumbItems = [
    { label: 'Anasayfa', href: '/' },
    { label: 'Hizmetlerimiz', href: '/#hizmetlerimiz' },
    { label: 'Sanayi Bölgesi Kamera Sistemi' }
  ];

  const faqData = [
    {
      question: "Sanayi bölgesi kamera sistemi kurulumu ne kadar sürer?",
      answer: "Tesis büyüklüğüne göre 2-10 gün arası değişir. Tuzla OSB projelerimizde ortalama 5 gün içinde tamamlıyoruz."
    },
    {
      question: "IP kamera sistemleri hangi özelliklere sahiptir?",
      answer: "4K çözünürlük, gece görüş, hareket algılama, uzaktan izleme ve mobil uygulama desteği sunar."
    },
    {
      question: "Hırsız alarm sistemleri nasıl çalışır?",
      answer: "Sensörler ve dedektörler ile izinsiz giriş anında uyarı verir, güvenlik firmalarına bildirim gönderir."
    },
    {
      question: "Geçiş kontrol sistemleri nedir?",
      answer: "Kart okuyucu, parmak izi veya yüz tanıma ile personel giriş-çıkışlarını kontrol eden sistemlerdir."
    },
    {
      question: "Yangın alarm sistemleri zorunlu mu?",
      answer: "Sanayi tesislerinde yasal zorunluluktur. Duman dedektörleri ve sprinkler sistemleri ile hayat kurtarır."
    }
  ];

  return (
    <>
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-24 pb-16 bg-gradient-to-b from-slate-900 to-slate-800">
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/bdc5d970de4f7a37a82b16b4dea07c20.jpg"
              alt="Tuzla OSB Sanayi Bölgesi"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-800/70 to-slate-900/80"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <Breadcrumb items={breadcrumbItems} />
            <div className="max-w-4xl mx-auto text-center text-white pt-8">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Sanayi Bölgesi Kamera Sistemi
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8">
                Tuzla OSB Sanayi Tesisleri İçin Profesyonel IP Kamera ve Güvenlik Sistemleri
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="tel:+905537165561" 
                  className="px-8 py-4 bg-cyan-500 text-white font-bold rounded-full hover:bg-cyan-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  +90 553 716 55 61
                </a>
                <a 
                  href="https://wa.me/905537165561" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-green-500 text-white font-bold rounded-full hover:bg-green-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Proje Detayları */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Tuzla OSB Kamera Sistemi Projesi
                </h2>
                <p className="text-gray-600 text-lg mb-6">
                  Tuzla Organize Sanayi Bölgesi'ndeki fabrikalar için özel olarak tasarlanmış IP kamera sistemi ve güvenlik altyapısı kurulum projemiz. Modern sanayi tesislerinin güvenlik ihtiyaçlarını en üst düzeyde karşılıyoruz.
                </p>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-500 mr-3 text-xl"></i>
                    <span><strong>IP kamera sistemleri</strong> ile 7/24 güvenlik</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-500 mr-3 text-xl"></i>
                    <span><strong>Hırsız alarm sistemleri</strong> ile koruma</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-500 mr-3 text-xl"></i>
                    <span><strong>Yangın alarm sistemleri</strong> ile güvenlik</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-500 mr-3 text-xl"></i>
                    <span><strong>Geçiş kontrol sistemleri</strong> ile erişim kontrolü</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-500 mr-3 text-xl"></i>
                    <span><strong>Turnike ve bariyer sistemleri</strong> ile güvenli geçiş</span>
                  </li>
                </ul>
              </div>
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20security%20cameras%20installation%20in%20Tuzla%20industrial%20facility%2C%20IP%20camera%20system%20setup%2C%20professional%20security%20equipment%2C%20factory%20surveillance%20system%2C%20technician%20installing%20cameras&width=800&height=600&seq=tuzla-camera-install&orientation=landscape"
                  alt="Tuzla Sanayi Kamera Kurulumu"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Güvenlik Sistemleri */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
              Sanayi Güvenlik Sistemlerimiz
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-camera-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">IP Kamera Sistemleri</h3>
                <p className="text-gray-600">
                  4K çözünürlük, gece görüş ve uzaktan erişim özellikli IP kamera sistemleri. Tuzla OSB fabrikaları için özel güvenlik çözümleri.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-alarm-warning-line text-2xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Hırsız Alarm Sistemleri</h3>
                <p className="text-gray-600">
                  PIR sensörler, manyetik kontaklar ve cam kırılma dedektörleri ile sanayi tesislerinizi koruyun.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-fire-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Yangın Alarm Sistemleri</h3>
                <p className="text-gray-600">
                  Duman dedektörleri, ısı sensörleri ve otomatik sprinkler sistemleri ile yangın güvenliği.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-door-lock-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Geçiş Kontrol Sistemleri</h3>
                <p className="text-gray-600">
                  Kart okuyucu, parmak izi ve yüz tanıma teknolojileri ile personel giriş-çıkış kontrolü.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-door-open-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Turnike Sistemleri</h3>
                <p className="text-gray-600">
                  Üç kollu ve tam boy turnike sistemleri ile güvenli personel geçişi ve sayım kontrolü.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-roadster-line text-2xl text-yellow-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Bariyer Sistemleri</h3>
                <p className="text-gray-600">
                  Araç giriş-çıkış kontrolü için otomatik bariyer sistemleri ve plaka tanıma teknolojisi.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Tuzla OSB Hakkında */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Tuzla%20Organize%20Sanayi%20Bolgesi%20factories%20and%20industrial%20facilities%2C%20modern%20manufacturing%20plants%2C%20organized%20industrial%20district%2C%20aerial%20view%20of%20Tuzla%20industrial%20zone&width=800&height=600&seq=tuzla-osb-view&orientation=landscape"
                  alt="Tuzla OSB Görünümü"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Tuzla OSB Güvenlik Altyapısı
                </h2>
                <p className="text-gray-600 text-lg mb-6">
                  Tuzla Organize Sanayi Bölgesi, İstanbul'un önemli sanayi merkezlerinden biridir. Bu bölgedeki fabrikaların güvenlik sistemleri ihtiyaçlarını profesyonelce karşılıyoruz.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <i className="ri-shield-check-line text-cyan-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Güvenlik Önceliği</h4>
                      <p className="text-gray-600">Sanayi tesisleri için kritik güvenlik altyapısı</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-eye-line text-cyan-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">7/24 İzleme</h4>
                      <p className="text-gray-600">Kesintisiz güvenlik kamerası monitörü</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-phone-line text-cyan-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Acil Müdahale</h4>
                      <p className="text-gray-600">Anında uyarı ve müdahale sistemi</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Teknoloji Özellikleri */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
              Gelişmiş Güvenlik Teknolojileri
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-hd-line text-3xl text-blue-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">4K Çözünürlük</h3>
                <p className="text-gray-600 text-sm">Ultra yüksek çözünürlük ile net görüntü</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-moon-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Gece Görüş</h3>
                <p className="text-gray-600 text-sm">Karanlıkta bile net görüntü kalitesi</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-smartphone-line text-3xl text-purple-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Mobil Erişim</h3>
                <p className="text-gray-600 text-sm">Telefondan uzaktan izleme imkanı</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-notification-line text-3xl text-red-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Anlık Uyarı</h3>
                <p className="text-gray-600 text-sm">Hareket algılandığında anında bildirim</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
              Sıkça Sorulan Sorular
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqData.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl shadow-lg p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h4>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Sanayi Güvenlik Sistemi İçin İletişime Geçin
            </h2>
            <p className="text-blue-100 text-xl mb-8 max-w-2xl mx-auto">
              Tuzla OSB'deki fabrikanız için profesyonel güvenlik kamerası ve alarm sistemleri. Hemen teklif alın.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+905537165561" 
                className="px-8 py-4 bg-white text-blue-600 font-bold rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                +90 553 716 55 61
              </a>
              <a 
                href="https://wa.me/905537165561" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-8 py-4 bg-green-500 text-white font-bold rounded-full hover:bg-green-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                WhatsApp İletişim
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}